import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Chip,
  TextField,
  Paper,
  Popper,
  ClickAwayListener,
  Checkbox,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Fade,
  styled,
  alpha,
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Close,
  Search,
  Add,
  Delete,
} from '@mui/icons-material';
import { ConfirmDialog } from './ConfirmDialog';

// ===== TYPES =====

export interface MultiselectItem {
  id: string | number;
  name: string;
  iconPath?: string;
  disabled?: boolean;
}

export interface MultiselectInputProps {
  /** Items to select from */
  items: MultiselectItem[];
  /** Currently selected items */
  selectedItems: MultiselectItem[];
  /** Callback when selection changes */
  onSelectedItemsChange: (items: MultiselectItem[]) => void;
  /** Allow creating new elements */
  shouldCreateElements?: boolean;
  /** Callback when new element is created */
  onElementCreated?: (name: string) => void;
  /** Callback when element is deleted */
  onElementDeleted?: (id: string | number) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Maximum dropdown height */
  maxHeight?: number;
  /** Disabled state */
  disabled?: boolean;
  /** Disable create button */
  disableCreate?: boolean;
  /** Disable delete buttons */
  disableDelete?: boolean;
  /** Hide remove button on chips */
  hideChipRemove?: boolean;
  /** Single select mode (radio-like behavior) */
  singleSelect?: boolean;
  /** Size variant */
  size?: 'small' | 'medium';
  /** Full width */
  fullWidth?: boolean;
  /** Error state */
  error?: boolean;
  /** Helper text */
  helperText?: string;
  /** Label */
  label?: string;
}

// ===== STYLED COMPONENTS =====

const StyledChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  height: 28,
  '& .MuiChip-label': {
    paddingLeft: 8,
    paddingRight: 8,
    fontSize: '0.8125rem',
    fontWeight: 500,
  },
  '& .MuiChip-deleteIcon': {
    color: alpha(theme.palette.common.white, 0.7),
    '&:hover': {
      color: theme.palette.common.white,
    },
  },
}));

const DropdownPaper = styled(Paper)(({ theme }) => ({
  marginTop: 4,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[8],
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
}));

const CreateButton = styled(ListItemButton)(({ theme }) => ({
  borderTop: `1px dashed ${theme.palette.divider}`,
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

// ===== COMPONENT =====

export function MultiselectInput({
  items = [],
  selectedItems = [],
  onSelectedItemsChange,
  shouldCreateElements = false,
  onElementCreated,
  onElementDeleted,
  placeholder = 'Выберите позиции',
  maxHeight = 300,
  disabled = false,
  disableCreate = false,
  disableDelete = false,
  hideChipRemove = false,
  singleSelect = false,
  size = 'small',
  fullWidth = true,
  error = false,
  helperText,
  label,
}: MultiselectInputProps) {
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | number | null>(null);

  // Refs
  const anchorRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Computed values
  const selectedIds = useMemo(
    () => new Set(selectedItems.map((item) => item.id)),
    [selectedItems]
  );

  const filteredItems = useMemo(() => {
    if (!searchText.trim()) return items;
    const search = searchText.toLowerCase();
    return items.filter((item) => item.name.toLowerCase().includes(search));
  }, [items, searchText]);

  const allSelected = useMemo(() => {
    const availableItems = items.filter((item) => !item.disabled);
    return availableItems.length > 0 && availableItems.every((item) => selectedIds.has(item.id));
  }, [items, selectedIds]);

  const someSelected = useMemo(() => {
    return items.some((item) => selectedIds.has(item.id) && !item.disabled);
  }, [items, selectedIds]);

  const isIndeterminate = someSelected && !allSelected;

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handlers
  const handleOpen = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
  }, [disabled]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchText('');
  }, []);

  const handleToggle = useCallback(() => {
    if (isOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [isOpen, handleOpen, handleClose]);

  const handleItemToggle = useCallback(
    (item: MultiselectItem) => {
      if (item.disabled) return;

      if (singleSelect) {
        // Single select mode: replace selection
        onSelectedItemsChange([item]);
      } else {
        // Multi select mode: toggle item
        const isSelected = selectedIds.has(item.id);
        if (isSelected) {
          onSelectedItemsChange(selectedItems.filter((i) => i.id !== item.id));
        } else {
          onSelectedItemsChange([...selectedItems, item]);
        }
      }
    },
    [singleSelect, selectedIds, selectedItems, onSelectedItemsChange]
  );

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      // Deselect all
      onSelectedItemsChange([]);
    } else {
      // Select all non-disabled items
      const availableItems = items.filter((item) => !item.disabled);
      onSelectedItemsChange(availableItems);
    }
  }, [allSelected, items, onSelectedItemsChange]);

  const handleRemoveItem = useCallback(
    (item: MultiselectItem, event?: React.MouseEvent) => {
      event?.stopPropagation();
      onSelectedItemsChange(selectedItems.filter((i) => i.id !== item.id));
    },
    [selectedItems, onSelectedItemsChange]
  );

  const handleCreateElement = useCallback(() => {
    if (searchText.trim() && onElementCreated) {
      onElementCreated(searchText.trim());
      setSearchText('');
    }
  }, [searchText, onElementCreated]);

  const handleDeleteClick = useCallback(
    (id: string | number, event: React.MouseEvent) => {
      event.stopPropagation();
      setItemToDelete(id);
      setDeleteConfirmOpen(true);
    },
    []
  );

  const handleDeleteConfirm = useCallback(() => {
    if (itemToDelete !== null && onElementDeleted) {
      onElementDeleted(itemToDelete);
      // Remove from selected if it was selected
      onSelectedItemsChange(selectedItems.filter((i) => i.id !== itemToDelete));
    }
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  }, [itemToDelete, onElementDeleted, selectedItems, onSelectedItemsChange]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  }, []);

  // Render chips
  const renderChips = () => {
    if (selectedItems.length === 0) {
      return (
        <Typography
          sx={{
            color: 'text.secondary',
            py: 0.5,
            userSelect: 'none',
          }}
        >
          {placeholder}
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {selectedItems.map((item) => (
          <StyledChip
            key={item.id}
            label={item.name}
            size="small"
            onDelete={
              hideChipRemove || disabled ? undefined : (e) => handleRemoveItem(item, e as React.MouseEvent)
            }
            deleteIcon={<Close sx={{ fontSize: 16 }} />}
          />
        ))}
      </Box>
    );
  };

  return (
    <>
      <Box ref={anchorRef} sx={{ width: fullWidth ? '100%' : 'auto' }}>
        {label && (
          <Typography
            variant="body2"
            sx={{
              mb: 0.5,
              fontWeight: 500,
              color: error ? 'error.main' : 'text.primary',
            }}
          >
            {label}
          </Typography>
        )}

        {/* Trigger Button */}
        <Box
          onClick={handleToggle}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            p: 1.25,
            minHeight: size === 'small' ? 42 : 56,
            border: 1,
            borderColor: error ? 'error.main' : isOpen ? 'primary.main' : 'divider',
            borderRadius: 1,
            backgroundColor: disabled ? 'action.disabledBackground' : 'background.paper',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'border-color 0.2s, box-shadow 0.2s',
            ...(isOpen && {
              boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
            }),
            '&:hover': {
              borderColor: disabled ? undefined : error ? 'error.main' : 'primary.main',
            },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>{renderChips()}</Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: isOpen ? 'primary.main' : 'action.active',
              transition: 'transform 0.2s',
              pt: 0.25,
            }}
          >
            {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </Box>
        </Box>

        {helperText && (
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              ml: 1.5,
              display: 'block',
              color: error ? 'error.main' : 'text.secondary',
            }}
          >
            {helperText}
          </Typography>
        )}

        {/* Dropdown */}
        <Popper
          open={isOpen}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorRef.current?.clientWidth }}
          transition
        >
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={handleClose}>
              <Fade {...TransitionProps} timeout={200}>
                <DropdownPaper>
                  {/* Search and Select All */}
                  <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                    {!singleSelect && (
                      <Checkbox
                        checked={allSelected}
                        indeterminate={isIndeterminate}
                        onChange={handleSelectAll}
                        size="small"
                        sx={{ p: 0.5 }}
                      />
                    )}

                    <SearchField
                      inputRef={searchInputRef}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder="Поиск..."
                      size="small"
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Search sx={{ color: 'action.active', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {/* Items List */}
                  <List
                    sx={{
                      maxHeight,
                      overflowY: 'auto',
                      py: 0.5,
                    }}
                  >
                    {filteredItems.map((item) => {
                      const isSelected = selectedIds.has(item.id);

                      return (
                        <ListItem
                          key={item.id}
                          disablePadding
                          secondaryAction={
                            shouldCreateElements && !item.disabled && !disableDelete ? (
                              <IconButton
                                size="small"
                                onClick={(e) => handleDeleteClick(item.id, e)}
                                sx={{ color: 'error.main' }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            ) : undefined
                          }
                        >
                          <ListItemButton
                            onClick={() => handleItemToggle(item)}
                            disabled={item.disabled}
                            selected={isSelected && singleSelect}
                            sx={{
                              py: 0.75,
                              '&.Mui-selected': {
                                backgroundColor: (theme) =>
                                  alpha(theme.palette.primary.main, 0.08),
                              },
                            }}
                          >
                            {!singleSelect && (
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Checkbox
                                  checked={isSelected}
                                  disabled={item.disabled}
                                  size="small"
                                  sx={{ p: 0 }}
                                />
                              </ListItemIcon>
                            )}

                            <ListItemText
                              primary={item.name}
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                                fontWeight: isSelected && singleSelect ? 600 : 400,
                                color: isSelected && singleSelect ? 'primary.main' : 'text.primary',
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}

                    {/* Empty state */}
                    {filteredItems.length === 0 && !searchText.trim() && (
                      <ListItem>
                        <ListItemText
                          primary="Нет доступных элементов"
                          primaryTypographyProps={{
                            color: 'text.secondary',
                            fontSize: '0.875rem',
                            textAlign: 'center',
                          }}
                        />
                      </ListItem>
                    )}

                    {/* Create new element option */}
                    {shouldCreateElements &&
                      searchText.trim() &&
                      !disableCreate &&
                      !filteredItems.some(
                        (item) => item.name.toLowerCase() === searchText.toLowerCase()
                      ) && (
                        <CreateButton onClick={handleCreateElement}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Add sx={{ color: 'primary.main' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <>
                                Создать "
                                <Box component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                  {searchText}
                                </Box>
                                "
                              </>
                            }
                            primaryTypographyProps={{
                              fontSize: '0.875rem',
                            }}
                          />
                        </CreateButton>
                      )}
                  </List>
                </DropdownPaper>
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Удаление элемента"
        message="Вы уверены, что хотите удалить этот элемент? Это действие нельзя отменить."
        confirmText="Удалить"
        cancelText="Отмена"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
}

export default MultiselectInput;
